// src/components/ui/cards/CommunicationsTemplateCard/CommunicationsTemplateCardShadcn.jsx
import React from 'react';
import { Mail, MessageSquare, CheckCircle, XCircle } from 'lucide-react';

// shadcn components
import { Card, CardContent } from '../../card';
import { Badge } from '../../badge';

import { cn } from '../../../../utils/cn';

const CommunicationsTemplateCardShadcn = ({
  template,
  templateType,
  isSelected = false,
  onClick
}) => {
  // Get human-readable trigger event name
  const getTriggerEventLabel = (templateKey) => {
    const eventLabels = {
      'appointment_confirmation': 'Appointment Confirmation',
      'appointment_reminder': 'Appointment Reminder',
      'appointment_cancellation': 'Appointment Cancellation',
      'appointment_reschedule': 'Appointment Reschedule',
      'invoice_notification': 'Invoice Notification'
    };

    return eventLabels[templateKey] || templateKey;
  };

  // Get template type configuration
  const getTypeConfig = (type) => {
    if (type === 'email') {
      return {
        icon: Mail,
        color: 'text-blue-400',
        selectedClasses: 'bg-blue-500/10 border-blue-500/50 ring-1 ring-blue-500/20'
      };
    } else {
      return {
        icon: MessageSquare,
        color: 'text-green-400',
        selectedClasses: 'bg-green-500/10 border-green-500/50 ring-1 ring-green-500/20'
      };
    }
  };

  const typeConfig = getTypeConfig(templateType);
  const TypeIcon = typeConfig.icon;

  return (
    <Card
      onClick={onClick}
      className={cn(
        "cursor-pointer transition-all duration-200 group",
        "hover:bg-accent/50",
        isSelected
          ? typeConfig.selectedClasses
          : "hover:shadow-md"
      )}
    >
      <CardContent className="p-3">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <TypeIcon className={cn("w-4 h-4 flex-shrink-0", typeConfig.color)} />
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-foreground truncate text-sm">
                {template.name}
              </h3>
              <p className="text-muted-foreground text-xs truncate">
                Trigger: {getTriggerEventLabel(template.template_key)}
              </p>
            </div>
          </div>

          {/* Status Indicator */}
          <Badge
            variant={template.active ? "default" : "secondary"}
            className={cn(
              "text-xs flex items-center gap-1",
              template.active
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-muted text-muted-foreground"
            )}
          >
            {template.active ? (
              <CheckCircle className="w-3 h-3" />
            ) : (
              <XCircle className="w-3 h-3" />
            )}
            <span>{template.active ? 'Active' : 'Inactive'}</span>
          </Badge>
        </div>

        {/* Template Preview - Hidden for now to keep cards compact */}
        <div className="space-y-2" style={{ height: '0px', overflow: 'hidden' }}>
          {templateType === 'email' && (
            <>
              <div>
                <p className="text-foreground text-sm truncate">
                  {template.subject || 'No subject'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs line-clamp-2 leading-relaxed">
                  {template.body_text || template.body_html?.replace(/<[^>]*>/g, '') || 'No content'}
                </p>
              </div>
            </>
          )}

          {templateType === 'sms' && (
            <div>
              <p className="text-foreground text-sm line-clamp-2 leading-relaxed">
                {template.message_body || 'No message'}
              </p>
              {(template.message_body || '').length > 160 && (
                <div className="flex items-center gap-1 mt-1">
                  <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                    {(template.message_body || '').length} chars
                  </Badge>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CommunicationsTemplateCardShadcn;